<template>
    <!-- The dark class here is important for all child components to use dark mode styles-->
    <div class="onboarding-v2-layout dark">
        <!-- LOADING INDICATOR -->
        <loading-indicator
            v-if="loading"
            class="vh-100 px-1"
            :title="loadingTitle"
        />

        <!-- MAIN CONTENT -->
        <div
            v-show="!loading"
            class="content-container"
            :style="containerStyle"
        >
            <!-- HEADER - Always takes up space even when empty -->
            <div
                v-if="hasHeader"
                class="header-section"
            >
                <a
                    v-if="hasBack"
                    @click="goBack"
                    @keydown.enter="goBack"
                    class="back-button"
                    event-name="click_onboarding_v2_back_button"
                >
                    <img
                        :src="backArrowIcon"
                        alt="Back"
                    >
                </a>
                <h5
                    v-if="pageTitle"
                    class="page-title"
                >
                    {{ pageTitle }}
                </h5>
            </div>

            <!-- PROGRESS BAR -->
            <progress-bar
                v-if="showProgressBar"
                :current-step="currentStep"
                :total-steps="totalSteps"
            />

            <!-- MAIN CONTENT AREA -->
            <div class="main-content">
                <!-- ADVICE BUBBLE -->
                <advice-bubble
                    v-if="adviceText"
                    :text="adviceText"
                    class="advice-section"
                />

                <!-- ERROR MESSAGE -->
                <div
                    v-if="errorText"
                    class="alert-container"
                    data-testid="onboarding-v2-error-text"
                >
                    <div
                        class="alert alert-warning text-center"
                        role="alert"
                    >
                        <span v-html="errorText" />
                    </div>
                </div>

                <!-- CONTENT SLOT -->
                <div class="content-slot">
                    <slot />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, getCurrentInstance, onMounted, onActivated, onBeforeUnmount, onDeactivated } from 'vue'
    import LoadingIndicator from '@/components/LoadingIndicator.vue'
    import ProgressBar from '@/components/onboardingV2/progressBar.vue'
    import AdviceBubble from '@/components/onboardingV2/adviceBubble.vue'
    import { getSafeAreaPaddingBottom, getSafeAreaPaddingTop } from '@/utils/safeAreaUtils'
    import { setSafeAreaConfig } from '@/nativeWebInteraction/nativeWebInteraction'
    import { log } from '@/utils/logger'
    import { logEvent } from '@/utils/http-client'
    import { appRoutePaths } from '@/routes/appRoutes'
    import backArrowIcon from '@/assets/images/onboarding-v2-back-icon.svg'

    interface Props {
        pageTitle?: string
        hasHeader?: boolean
        hasBack?: boolean
        backAction?: (() => void) | null
        loading?: boolean
        loadingTitle?: string
        errorText?: string
        backgroundColor?: string
        showProgressBar?: boolean
        currentStep?: number
        totalSteps?: number
        adviceText?: string
    }

    const props = withDefaults(defineProps<Props>(), {
        pageTitle: '',
        hasHeader: true,
        hasBack: true,
        backAction: null,
        loading: false,
        loadingTitle: 'Loading...',
        errorText: '',
        backgroundColor: 'black',
        showProgressBar: false,
        currentStep: 1,
        totalSteps: 5,
        adviceText: '',
    })

    const instance = getCurrentInstance()
    const router = instance?.proxy.$router

    const containerStyle = computed(() => {
        // Ensure minimum top padding for web browsers without safe area insets
        const topPadding = Math.max(getSafeAreaPaddingTop(), 16)
        let style = `padding-top: ${topPadding}px; padding-bottom: ${getSafeAreaPaddingBottom()}px;`
        if (props.backgroundColor) {
            style += `background-color: ${props.backgroundColor};`
        }
        return style
    })

    const goBack = () => {
        logEvent('click_advisor_onboarding_v2_back_button')
        if (props.backAction) {
            log.info('OnboardingV2Layout has a back action, so executing that on back click')
            props.backAction()
        } else {
            log.info('OnboardingV2Layout does not have a back action, so calling router.back()')
            if (window.history.length > 1) {
                log.info('OnboardingV2Layout has history, so calling router.back()')
                router?.back()
            } else {
                log.info('OnboardingV2Layout does not have history, so calling router.replace()')
                router?.replace(appRoutePaths.TODAY)
            }
        }
    }

    // Set safe area config to black background when mounted
    onMounted(async () => {
        await setSafeAreaConfig({ enabled: true, backgroundColor: '#000000' })
    })

    // Also set safe area config when component is activated (returns from cache)
    onActivated(async () => {
        await setSafeAreaConfig({ enabled: true, backgroundColor: '#000000' })
    })

    // Reset safe area config when component is destroyed
    onBeforeUnmount(async () => {
        await setSafeAreaConfig({ enabled: true, backgroundColor: '#FFFFFF' })
    })

    // Reset safe area config when component is deactivated (cached)
    onDeactivated(async () => {
        await setSafeAreaConfig({ enabled: true, backgroundColor: '#FFFFFF' })
    })
</script>

<style scoped>
    .onboarding-v2-layout {
        min-height: 100vh;
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        overflow-x: hidden;
        background-color: black;
        border-radius: 0 !important;
        flex: 1;
    }

    .content-container {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 100%;
        background-color: black;
    }

    .header-section {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 64px;
        min-height: 64px;
        flex-shrink: 0;
        background-color: black;
    }

    .back-button {
        position: absolute;
        left: 24px;
        top: 50%;
        transform: translateY(-50%);
        width: 20px;
        height: 20px;
        cursor: pointer;
        z-index: 10;
    }

    .back-button img {
        display: block;
        width: 100%;
        height: 100%;
    }

    .page-title {
        font-family: 'Proxima Nova', sans-serif;
        font-size: 20px;
        font-weight: 400;
        line-height: 28px;
        color: white;
        text-align: center;
        margin: 0;
    }

    .main-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        background-color: black;
        overflow-y: auto;
        min-height: 0;
    }

    .advice-section {
        padding: 24px;
        flex-shrink: 0;
    }

    .alert-container {
        padding: 0 24px;
        flex-shrink: 0;
    }

    .content-slot {
        flex: 1;
        padding: 24px;
        display: flex;
        flex-direction: column;
    }
</style>
